import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import SellerLayout from "@/components/SellerLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Upload, Download, FileText, X, CheckCircle, AlertCircle, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BulkOrders = ({ user, onLogout }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [pollingJobIds, setPollingJobIds] = useState(new Set());
  
  // Wallet balance state
  const [walletBalance, setWalletBalance] = useState(0);
  const [minWalletBalance, setMinWalletBalance] = useState(0);
  const [isLowBalance, setIsLowBalance] = useState(false);

  useEffect(() => {
    fetchJobs();
    fetchWalletInfo();
  }, [currentPage]);

  useEffect(() => {
    // Poll for active jobs
    const interval = setInterval(() => {
      if (pollingJobIds.size > 0) {
        pollingJobIds.forEach(jobId => {
          fetchJobStatus(jobId);
        });
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [pollingJobIds]);

  const fetchWalletInfo = async () => {
    try {
      const response = await axios.get(`${API}/wallet/balance`);
      const balance = response.data.balance || 0;
      const minBalance = response.data.min_wallet_balance || 0;
      const enforceWalletMinimum = response.data.enforce_wallet_minimum_for_orders || false;
      setWalletBalance(balance);
      setMinWalletBalance(minBalance);
      // Only flag as low balance if enforcement is enabled
      setIsLowBalance(enforceWalletMinimum && balance <= minBalance);
    } catch (error) {
      console.error("Failed to fetch wallet info");
    }
  };

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/bulk-orders/jobs?page=${currentPage}&limit=20`);
      setJobs(response.data.jobs);
      setPagination(response.data.pagination);

      // Set polling for processing jobs
      const processingJobs = response.data.jobs
        .filter(job => job.status === "processing" || job.status === "pending")
        .map(job => job.job_id);
      setPollingJobIds(new Set(processingJobs));
    } catch (error) {
      console.error("Failed to fetch jobs:", error);
      toast.error("Failed to load bulk order jobs");
    } finally {
      setLoading(false);
    }
  };

  const fetchJobStatus = async (jobId) => {
    try {
      const response = await axios.get(`${API}/bulk-orders/jobs/${jobId}`);
      setJobs(prevJobs =>
        prevJobs.map(job => (job.job_id === jobId ? response.data : job))
      );

      // Remove from polling if completed/failed/cancelled
      if (["completed", "failed", "cancelled"].includes(response.data.status)) {
        setPollingJobIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(jobId);
          return newSet;
        });
      }
    } catch (error) {
      console.error(`Failed to fetch status for job ${jobId}:`, error);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv') && !file.name.endsWith('.xlsx')) {
        toast.error("Please select a CSV or Excel (.xlsx) file");
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select a file first");
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", selectedFile);

      const response = await axios.post(`${API}/bulk-orders/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success(response.data.message);
      setSelectedFile(null);
      setCurrentPage(1);
      fetchJobs();
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error(error.response?.data?.detail || "Failed to upload file");
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/bulk-orders/template`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "bulk_order_template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Template downloaded successfully");
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Failed to download template");
    }
  };

  const handleDownloadLocations = async () => {
    try {
      const response = await axios.get(`${API}/bulk-orders/locations-reference`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "location_ids_reference.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Location IDs reference downloaded");
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Failed to download location IDs");
    }
  };

  const handleCancelJob = async (jobId) => {
    try {
      await axios.post(`${API}/bulk-orders/jobs/${jobId}/cancel`);
      toast.success("Job cancelled successfully");
      fetchJobs();
    } catch (error) {
      console.error("Cancel failed:", error);
      toast.error(error.response?.data?.detail || "Failed to cancel job");
    }
  };

  const handleDownloadErrors = async (jobId) => {
    try {
      const response = await axios.get(`${API}/bulk-orders/jobs/${jobId}/download-errors`);
      
      // Check if we got a download URL (S3) or blob (legacy)
      if (response.data.download_url) {
        // Open the presigned URL in a new tab to download
        window.open(response.data.download_url, '_blank');
        toast.success("Error report download started");
      } else {
        // Legacy blob response
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `errors_${jobId}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success("Error report downloaded");
      }
    } catch (error) {
      console.error("Download failed:", error);
      toast.error(error.response?.data?.detail || "Failed to download error report");
    }
  };

  const handleDownloadResults = async (jobId) => {
    try {
      const response = await axios.get(`${API}/bulk-orders/jobs/${jobId}/download-results`);
      
      // Check if we got a download URL (S3) or blob (legacy)
      if (response.data.download_url) {
        // Open the presigned URL in a new tab to download
        window.open(response.data.download_url, '_blank');
        toast.success("Results file download started");
      } else {
        // Legacy blob response
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `results_${jobId}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success("Results file downloaded");
      }
    } catch (error) {
      console.error("Download failed:", error);
      toast.error(error.response?.data?.detail || "Failed to download results file");
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: "bg-yellow-100 text-yellow-800", icon: Clock, text: "Pending" },
      processing: { color: "bg-blue-100 text-blue-800", icon: Loader2, text: "Processing" },
      completed: { color: "bg-green-100 text-green-800", icon: CheckCircle, text: "Completed" },
      failed: { color: "bg-red-100 text-red-800", icon: AlertCircle, text: "Failed" },
      cancelled: { color: "bg-gray-100 text-gray-800", icon: X, text: "Cancelled" },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge className={config.color}>
        <Icon className="w-3 h-3 mr-1" />
        {config.text}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    // Ensure the date string is treated as UTC by appending 'Z' if not present
    let utcDateString = dateString;
    if (typeof dateString === 'string' && !dateString.endsWith('Z') && !dateString.includes('+')) {
      utcDateString = dateString + 'Z';
    }
    const date = new Date(utcDateString);
    return date.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });
  };

  return (
    <SellerLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Bulk Order Creation</h1>
          <p className="text-sm sm:text-base text-slate-600">Upload CSV files to create multiple orders at once</p>
        </div>

        {/* Upload Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Upload Bulk Orders
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Template Download */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-3">
                <FileText className="w-8 h-8 text-blue-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-slate-900">Need a template?</p>
                  <p className="text-sm text-slate-600">Download our CSV template with sample data</p>
                </div>
              </div>
              <Button variant="outline" onClick={handleDownloadTemplate} className="w-full sm:w-auto flex-shrink-0">
                <Download className="w-4 h-4 mr-2" />
                Download Template
              </Button>
            </div>

            {/* Low Balance Warning */}
            {isLowBalance && (
              <div className="flex items-center gap-3 p-4 bg-red-50 rounded-lg border border-red-200">
                <div className="flex-1">
                  <p className="font-medium text-red-800">Low Wallet Balance</p>
                  <p className="text-sm text-red-600">
                    Your wallet balance (₹{walletBalance.toLocaleString()}) is below the minimum threshold (₹{minWalletBalance.toLocaleString()}). 
                    Please recharge your wallet to create bulk orders.
                  </p>
                </div>
              </div>
            )}

            {/* File Upload */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Select CSV or Excel File</label>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="file"
                  accept=".csv,.xlsx"
                  onChange={handleFileSelect}
                  disabled={isLowBalance}
                  className={`flex-1 min-w-0 px-3 py-2 border border-slate-300 rounded-md text-sm ${isLowBalance ? 'bg-slate-100 cursor-not-allowed opacity-60' : ''}`}
                />
                <Button
                  onClick={handleUpload}
                  disabled={!selectedFile || uploading || isLowBalance}
                  style={{ backgroundColor: isLowBalance ? '#9CA3AF' : "#00A699" }}
                  className={`w-full sm:w-auto flex-shrink-0 ${isLowBalance ? 'cursor-not-allowed' : ''}`}
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload & Process
                    </>
                  )}
                </Button>
              </div>
              {selectedFile && (
                <p className="text-sm text-slate-600">
                  Selected: <span className="font-medium">{selectedFile.name}</span>
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Jobs History */}
        <Card>
          <CardHeader>
            <CardTitle>Upload History</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading && jobs.length === 0 ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
                <p className="text-slate-500 mt-2">Loading...</p>
              </div>
            ) : jobs.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 mx-auto text-slate-300" />
                <p className="text-slate-500 mt-2">No bulk uploads yet</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">File Name</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Progress</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Total Rows</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Success</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Failed</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Created At</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobs.map((job) => (
                        <tr key={job.job_id} className="border-b hover:bg-slate-50">
                          <td className="px-4 py-3 align-middle text-sm">{job.file_name}</td>
                          <td className="px-4 py-3 align-middle text-sm">{getStatusBadge(job.status)}</td>
                          <td className="px-4 py-3 align-middle text-sm">
                            {job.status === "processing" || job.status === "pending" ? (
                              <div className="space-y-1">
                                <div className="w-32 bg-gray-200 rounded-full h-2">
                                  <div
                                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${job.progress_percentage || 0}%` }}
                                  ></div>
                                </div>
                                <p className="text-xs text-slate-600">{(job.progress_percentage || 0).toFixed(1)}%</p>
                              </div>
                            ) : (
                              <span className="text-slate-500">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 align-middle text-sm text-right">{job.total_rows}</td>
                          <td className="px-4 py-3 align-middle text-sm text-right text-green-600 font-medium">
                            {job.successful_orders}
                          </td>
                          <td className="px-4 py-3 align-middle text-sm text-right text-red-600 font-medium">
                            {job.failed_rows}
                          </td>
                          <td className="px-4 py-3 align-middle text-sm text-slate-600 whitespace-nowrap">
                            <div>{formatDate(job.created_at).split(', ')[0]}</div>
                            <div className="text-xs text-slate-500">{formatDate(job.created_at).split(', ')[1]}</div>
                          </td>
                          <td className="px-4 py-3 align-middle text-sm">
                            <div className="flex gap-1 justify-center">
                              {job.status === "completed" && job.failed_rows > 0 && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDownloadErrors(job.job_id)}
                                  title="Download Error Report"
                                >
                                  <AlertCircle className="w-3 h-3" />
                                </Button>
                              )}
                              {job.status === "completed" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDownloadResults(job.job_id)}
                                  title="Download Results"
                                >
                                  <Download className="w-3 h-3" />
                                </Button>
                              )}
                              {(job.status === "processing" || job.status === "pending") && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleCancelJob(job.job_id)}
                                  title="Cancel Job"
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagination.pages > 1 && (
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 px-4 py-3 border-t mt-4">
                    <div className="text-xs sm:text-sm text-slate-600 text-center sm:text-left">
                      Showing {Math.min((currentPage - 1) * pagination.limit + 1, pagination.total)} to{" "}
                      {Math.min(currentPage * pagination.limit, pagination.total)} of {pagination.total} jobs
                    </div>
                    <div className="flex gap-2 justify-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </Button>
                      <div className="flex items-center gap-1">
                        {[...Array(pagination.pages)].map((_, i) => {
                          const pageNum = i + 1;
                          if (
                            pageNum === 1 ||
                            pageNum === pagination.pages ||
                            (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                          ) {
                            return (
                              <Button
                                key={pageNum}
                                variant={currentPage === pageNum ? "default" : "outline"}
                                size="sm"
                                onClick={() => setCurrentPage(pageNum)}
                                style={currentPage === pageNum ? { backgroundColor: "#00A699" } : {}}
                              >
                                {pageNum}
                              </Button>
                            );
                          } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                            return <span key={pageNum} className="px-2">...</span>;
                          }
                          return null;
                        })}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === pagination.pages}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </SellerLayout>
  );
};

export default BulkOrders;
